/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { EntityCompositionSetting } from 'src/core/types/EntitySwagger';
import { generateEntityColorFromString } from 'src/core/utils/color';
import { ModelType } from 'src/luis/types/ModelType';

type EntityStoreData = Partial<OmitFunctions<EntityStore>>;
export type SublistEntityCapability = {
    id: string;
    listKey: string;
    synonyms: {
        language: string;
        values: string[];
    }[];
};
export type PrebuiltEntityCapability = {
    id: string;
    category: string;
};
export type RegexEntityCapability = {
    id: string;
    regexKey: string;
    language: string;
    expression: string;
};
export type EntityCapability = 'learned' | 'list' | 'prebuilt' | 'regex';

export class EntityStore {
    public id: string;

    public name: string;

    public color: string;

    public readonly typeId: ModelType.Simple = ModelType.Simple;

    @observable public compositionSetting: EntityCompositionSetting;

    @observable public sublists: SublistEntityCapability[];

    @observable public prebuilts: PrebuiltEntityCapability[];

    @observable public requiredComponents: string[];

    @observable public regexList: RegexEntityCapability[];

    constructor(entityData: EntityStoreData) {
        this.id = entityData.name;
        this.name = entityData.name;
        this.color = generateEntityColorFromString(entityData.name);
        this.compositionSetting = entityData.compositionSetting ?? 'combineComponents';
        this.sublists = entityData.sublists ?? [];
        this.prebuilts = entityData.prebuilts ?? [];
        this.requiredComponents = entityData.requiredComponents ?? [];
        this.regexList = entityData.regexList ?? [];
    }

    @action
    public setCompositionSetting(entityCombination: EntityCompositionSetting) {
        this.compositionSetting = entityCombination;
    }

    @action
    public setSublists(sublists: SublistEntityCapability[]) {
        this.sublists = sublists;
    }

    @action
    public deleteSublist(ids: string[]) {
        this.sublists = this.sublists.filter(sublist => ids.includes(sublist.id));
    }

    @action
    public setPrebuilts(prebuilts: PrebuiltEntityCapability[]) {
        this.prebuilts = prebuilts;
    }

    @action
    public setRequiredComponents(requiredComponents: string[]) {
        this.requiredComponents = requiredComponents;
    }

    @action
    public setRegexList(list: RegexEntityCapability[]) {
        this.regexList = list;
    }

    @action
    public deletePrebuilts(ids: string[]) {
        this.prebuilts = this.prebuilts.filter(prebuilt => ids.includes(prebuilt.id));
    }

    @action
    public capabilities(labelsCount: number) {
        return <EntityCapability[]>(labelsCount ? ['learned'] : [])
            .concat(this.sublists.length > 0 ? 'list' : [])
            .concat(this.prebuilts.length > 0 ? 'prebuilt' : [])
            .concat(this.regexList.length > 0 ? 'regex' : []);
    }

    /**
     * Renames the entity
     * @param newName
     */
    @action
    public renameEntity(newName: string) {
        this.name = newName;
    }
}
